/**
 * Services Hub Page
 *
 * Central dashboard showing all available HitSearch services.
 * Users land here after successful login.
 */

import React from 'react';
import { Navbar, Card, ServerHealthWidget, ContainersWidget } from '../components';
import { useAuthStore } from '../store/authStore';
import type { ServiceCard } from '../types';

export const ServicesPage: React.FC = () => {
  const { user } = useAuthStore();

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  // Define available services
  // In the future, this could be fetched from an API
  const allServices: ServiceCard[] = [
    {
      id: 'ai-content-creator-prod',
      name: 'AI Content Creator',
      description: 'Generate and manage AI-powered content with multiple providers',
      url: 'https://ai-api.hitsearch.biz',
      icon: '🤖',
      environment: 'production',
      status: 'active',
    },
    {
      id: 'ai-content-creator-dev',
      name: 'AI Content Creator (Dev)',
      description: 'Development environment for testing new features',
      url: 'https://dev-ai-api.hitsearch.biz',
      icon: '🧪',
      environment: 'development',
      status: 'active',
    },
    {
      id: 'monitoring',
      name: 'Server Monitoring',
      description: 'Uptime monitoring and status dashboard',
      url: 'https://monitor.hitsearch.biz',
      icon: '📊',
      status: 'active',
    },
    // Temporarily hidden from the services dashboard for all users.
    // Service + files remain deployed; uncomment to restore the card.
    // {
    //   id: 'scraper-translator',
    //   name: 'Scraper Translator for 888Casino Migration',
    //   description: 'Scrape web pages and translate content to Arabic with Claude',
    //   url: 'https://scraper-translator.hitsearch.biz',
    //   icon: '🌐',
    //   status: 'active',
    // },
  ];

  // Filter services based on user role
  // Only admins can see development environments and admin-only services
  const services = allServices.filter(service => {
    if (service.adminOnly) {
      return isAdmin;  // Only admins see admin-only services
    }
    if (service.environment === 'development') {
      return isAdmin;  // Only admins see dev environments
    }
    return true;  // Everyone sees production services
  });

  const pageStyles: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: 'var(--bg-primary)',
  };

  const containerStyles: React.CSSProperties = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 20px',
  };

  const headerStyles: React.CSSProperties = {
    marginBottom: '32px',
  };

  const titleStyles: React.CSSProperties = {
    fontSize: '32px',
    fontWeight: 600,
    marginBottom: '8px',
    color: 'var(--text-primary)',
  };

  const subtitleStyles: React.CSSProperties = {
    fontSize: '16px',
    color: 'var(--text-secondary)',
  };

  const gridStyles: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '24px',
    alignItems: 'stretch',  // Make all grid items same height
  };

  const serviceCardStyles: React.CSSProperties = {
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    height: '100%',  // Card fills grid item height
    display: 'flex',
    flexDirection: 'column',
  };

  const serviceCardHoverStyles: React.CSSProperties = {
    transform: 'translateY(-4px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  };

  const iconStyles: React.CSSProperties = {
    fontSize: '48px',
    marginBottom: '16px',
  };

  const serviceNameStyles: React.CSSProperties = {
    fontSize: '20px',
    fontWeight: 600,
    marginBottom: '8px',
    color: 'var(--text-primary)',
  };

  const serviceDescStyles: React.CSSProperties = {
    fontSize: '14px',
    color: 'var(--text-secondary)',
    marginBottom: '16px',
    lineHeight: '1.5',
  };

  const badgeStyles: React.CSSProperties = {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
  };

  const envBadgeStyles = (env?: 'production' | 'development'): React.CSSProperties => ({
    ...badgeStyles,
    backgroundColor: env === 'production' ? '#e8f5e9' : '#fff3e0',
    color: env === 'production' ? '#2e7d32' : '#e65100',
  });

  const handleServiceClick = (url: string) => {
    // Pass auth token to service and open in new tab
    const token = localStorage.getItem('auth_token');
    if (token) {
      // Open in new tab with token
      window.open(`${url}?token=${token}`, '_blank', 'noopener,noreferrer');
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const [hoveredCard, setHoveredCard] = React.useState<string | null>(null);

  return (
    <div style={pageStyles}>
      <Navbar />

      <div style={containerStyles}>
        <div style={headerStyles}>
          <h1 style={titleStyles}>
            Welcome back, {user?.name?.split(' ')[0] || 'User'}! 👋
          </h1>
          <p style={subtitleStyles}>
            Choose a service to get started
          </p>
        </div>

        <div style={gridStyles}>
          {services.map((service) => (
            <div
              key={service.id}
              onClick={() => handleServiceClick(service.url)}
              onMouseEnter={() => setHoveredCard(service.id)}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                ...serviceCardStyles,
                ...(hoveredCard === service.id ? serviceCardHoverStyles : {}),
              }}
            >
              <Card>
                <div style={iconStyles}>{service.icon}</div>
                <h3 style={serviceNameStyles}>{service.name}</h3>
                <p style={serviceDescStyles}>{service.description}</p>
                {service.environment && (
                  <span style={envBadgeStyles(service.environment)}>
                    {service.environment}
                  </span>
                )}
              </Card>
            </div>
          ))}
        </div>

        {isAdmin && (
          <div style={{ marginTop: '32px' }}>
            <ServerHealthWidget />
          </div>
        )}

        {isAdmin && (
          <div style={{ marginTop: '24px' }}>
            <ContainersWidget />
          </div>
        )}
      </div>
    </div>
  );
};
